import ArticleService from './ArticleService';
import BrandService from './BrandService';
import CustomerService from './CustomerService';
import DeliveryAddressService from './DeliveryAddressService';
import DiscountService from './DiscountService';
import IndentService from './IndentService';
import IndentEntryService from './IndentEntryService';
import StatisticsService from './StatisticsService';
import UserService from './UserService';

/**
 * AICommandService
 * 
 * This service provides a structured list of available commands that the AI can use
 * to retrieve data from the system. It focuses on GET requests to ensure read-only
 * operations for safety.
 */
class AICommandService {
  constructor() {
    // Initialize the commands map
    this.commands = this.buildCommandsMap();
  }

  /**
   * Build a structured map of all available GET commands from services
   */
  buildCommandsMap() {
    return {
      articles: {
        getAll: {
          description: "Get all articles",
          method: ArticleService.getAll,
          parameters: [],
          example: "Get a list of all articles"
        },
        getAllActive: {
          description: "Get all active articles",
          method: ArticleService.getAllActiveArticles,
          parameters: [],
          example: "Get a list of all active articles"
        },
        getById: {
          description: "Get article by ID",
          method: ArticleService.get,
          parameters: ["id"],
          example: "Get article with ID 123"
        },
        findByName: {
          description: "Find articles by name",
          method: ArticleService.findByName,
          parameters: ["articleName"],
          example: "Find articles with name 'Shirt'"
        },
        searchByName: {
          description: "Search articles by partial name match",
          method: ArticleService.searchArticlesByName,
          parameters: ["name"],
          example: "Search for articles containing 'cotton'"
        },
        activateArticle: {
          description: "Activate an article by ID",
          method: ArticleService.activateArticle,
          parameters: ["id"],
          example: "Activate article with ID 123"
        },
        deactivateArticle: {
          description: "Deactivate an article by ID",
          method: ArticleService.deactivateArticle,
          parameters: ["id"],
          example: "Deactivate article with ID 123"
        },
        activateArticleByName: {
          description: "Activate an article by name",
          method: ArticleService.activateArticleByName,
          parameters: ["name"],
          example: "Activate article 'Summer T-shirt'"
        },
        deactivateArticleByName: {
          description: "Deactivate an article by name",
          method: ArticleService.deactivateArticleByName,
          parameters: ["name"],
          example: "Deactivate article 'Winter Jacket'"
        },
        showArticle: {
          description: "Make an article visible by ID",
          method: ArticleService.showArticle,
          parameters: ["id"],
          example: "Make article with ID 123 visible"
        },
        hideArticle: {
          description: "Make an article invisible by ID",
          method: ArticleService.hideArticle,
          parameters: ["id"],
          example: "Make article with ID 123 invisible"
        },
        showArticleByName: {
          description: "Make an article visible by name",
          method: ArticleService.showArticleByName,
          parameters: ["name"],
          example: "Make article 'Blue Jeans' visible"
        },
        hideArticleByName: {
          description: "Make an article invisible by name",
          method: ArticleService.hideArticleByName,
          parameters: ["name"],
          example: "Make article 'Red Shirt' invisible"
        },
        getLatestArticles: {
          description: "Get all articles sorted by creation time (newest first)",
          method: async () => {
            try {
              const response = await ArticleService.getAll();
              const articles = response.data;
              return articles.sort((a, b) => new Date(b.creationTime) - new Date(a.creationTime));
            } catch (error) {
              console.error("Error getting latest articles:", error);
              throw error;
            }
          },
          parameters: [],
          example: "What's the newest article?"
        },
        getArticlesByBrand: {
          description: "Get all articles filtered by brand name",
          method: async (brandName) => {
            try {
              const response = await ArticleService.getAll();
              const articles = response.data;
              return articles.filter(article => 
                article.brand && article.brand.brandName.toLowerCase().includes(brandName.toLowerCase())
              );
            } catch (error) {
              console.error("Error getting articles by brand:", error);
              throw error;
            }
          },
          parameters: ["brandName"],
          example: "Show me all articles from Nike brand"
        }
      },
      brands: {
        getAll: {
          description: "Get all brands",
          method: BrandService.getAll,
          parameters: [],
          example: "Get a list of all brands"
        },
        getById: {
          description: "Get brand by ID",
          method: BrandService.get,
          parameters: ["id"],
          example: "Get brand with ID 123"
        },
        findByName: {
          description: "Find brands by name",
          method: BrandService.findByName,
          parameters: ["brandName"],
          example: "Find brands with name 'Nike'"
        }
      },
      customers: {
        getAll: {
          description: "Get all customers",
          method: CustomerService.getAll,
          parameters: [],
          example: "Get a list of all customers"
        },
        getById: {
          description: "Get customer by ID",
          method: CustomerService.get,
          parameters: ["id"],
          example: "Get customer with ID 123"
        },
        findByName: {
          description: "Find customers by name",
          method: CustomerService.findByName,
          parameters: ["clientName"],
          example: "Find customers with name 'Acme Corp'"
        },
        findByEmail: {
          description: "Find customers by email",
          method: CustomerService.findByEmail,
          parameters: ["email"],
          example: "Find customer with email 'john@example.com'"
        }
      },
      indents: {
/*         getAll: {
          description: "Get all indents",
          method: IndentService.getAll,
          parameters: [],
          example: "Get a list of all indents"
        }, */
        getAllActive: {
          description: "Get all active indents",
          method: IndentService.getAllActiveIndents,
          parameters: [],
          example: "Get a list of all active indents"
        },
/*         getAllInactive: {
          description: "Get all inactive indents",
          method: IndentService.getAllInactiveIndents,
          parameters: [],
          example: "Get a list of all inactive indents"
        }, */
        getAllIndents: {
          description: "Get all indents (both active and inactive)",
          method: IndentService.getAllIndents,
          parameters: [],
          example: "Get a list of all indents regardless of status"
        },
        getById: {
          description: "Get indent by ID",
          method: IndentService.get,
          parameters: ["id"],
          example: "Get indent with ID 123"
        },
        findByCode: {
          description: "Find indents by code",
          method: IndentService.findByCode,
          parameters: ["code"],
          example: "Find indent with code 'ORD-2023-001'"
        },
/*         getRecentIndents: {
          description: "Get indents sorted by date (newest first)",
          method: async () => {
            try {
              const response = await IndentService.getAll();
              const indents = response.data;
              return indents.sort((a, b) => new Date(b.creationTime || b.created) - new Date(a.creationTime || a.created));
            } catch (error) {
              console.error("Error getting recent indents:", error);
              throw error;
            }
          },
          parameters: [],
          example: "What are the most recent orders?"
        }, */
/*         getIndentsByCustomer: {
          description: "Get indents filtered by customer name",
          method: async (customerName) => {
            try {
              const response = await IndentService.getAll();
              const indents = response.data;
              return indents.filter(indent => 
                indent.customer && 
                indent.customer.clientName && 
                indent.customer.clientName.toLowerCase().includes(customerName.toLowerCase())
              );
            } catch (error) {
              console.error("Error getting indents by customer:", error);
              throw error;
            }
          },
          parameters: ["customerName"],
          example: "Show me all orders from Acme Corp"
        }, */
        findByCustomerName: {
          description: "Find indents by customer name",
          method: IndentService.findByCustomerName,
          parameters: ["customerName"],
          example: "Find indents for customer 'Acme Corp'"
        },
        getActiveIndentsByCustomerName: {
          description: "Get active indents for a specific customer",
          method: IndentService.getActiveIndentsByCustomerName,
          parameters: ["customerName"],
          example: "Get active indents for customer 'Acme Corp'"
        },
        getPendingIndentsByCustomerName: {
          description: "Get pending indents for a specific customer",
          method: IndentService.getPendingIndentsByCustomerName,
          parameters: ["customerName"],
          example: "Get pending indents for customer 'Acme Corp'"
        }
      },
      indentEntries: {
        getAll: {
          description: "Get all indent entries",
          method: IndentEntryService.getAll,
          parameters: [],
          example: "Get all indent entries"
        },
        getByIndentCode: {
          description: "Get indent entries by indent code",
          method: IndentEntryService.getIndentEntries,
          parameters: ["indentCode"],
          example: "Get entries for indent code 'ORD-2023-001'"
        }
      },
      statistics: {
        getYears: {
          description: "Get years available for statistics",
          method: StatisticsService.getYears,
          parameters: [],
          example: "Get all years available for statistics"
        },
        getOrderStats: {
          description: "Get order statistics for analysis",
          method: async (year) => {
            try {
              // Get the current year if not specified
              const targetYear = year || new Date().getFullYear().toString();
              const response = await StatisticsService.getOrderedQuantityAndRevenueForGivenYearByMonths(targetYear);
              return response.data;
            } catch (error) {
              console.error("Error getting order statistics:", error);
              throw error;
            }
          },
          parameters: ["year"],
          example: "Show me sales statistics for 2023"
        }
      },
      deliveryAddresses: {
        getAll: {
          description: "Get all delivery addresses",
          method: DeliveryAddressService.getAll,
          parameters: [],
          example: "Get all delivery addresses"
        },
        getClientAddresses: {
          description: "Get all delivery addresses for the current client",
          method: DeliveryAddressService.findClientsDeliveryAddresses,
          parameters: [],
          example: "Show me my delivery addresses"
        }
      },
      discounts: {
        getAll: {
          description: "Get all discounts",
          method: DiscountService.getAll,
          parameters: [],
          example: "Get all discounts"
        },
        getClientDiscounts: {
          description: "Get all discounts for the current client",
          method: DiscountService.findClientsDiscounts,
          parameters: [],
          example: "Show me my discounts"
        }
      }
    };
  }

  /**
   * Get all available commands
   * @returns {Object} Map of all available commands
   */
  getAllCommands() {
    return this.commands;
  }

  /**
   * Get commands for a specific service
   * @param {string} serviceName - Name of the service
   * @returns {Object} Commands for the specified service
   */
  getServiceCommands(serviceName) {
    return this.commands[serviceName] || {};
  }

  /**
   * Execute a command
   * @param {string} serviceName - Name of the service
   * @param {string} commandName - Name of the command
   * @param {Array} params - Parameters for the command
   * @returns {Promise} Promise that resolves with the command result
   */
  async executeCommand(serviceName, commandName, params = []) {
    try {
      console.log(`AICommandService.executeCommand called with:`, {
        serviceName,
        commandName,
        params,
        paramsType: typeof params,
        isArray: Array.isArray(params)
      });
      
      const service = this.commands[serviceName];
      if (!service) {
        console.error(`Service '${serviceName}' not found`);
        throw new Error(`Service '${serviceName}' not found`);
      }

      const command = service[commandName];
      if (!command) {
        console.error(`Command '${commandName}' not found in service '${serviceName}'`);
        throw new Error(`Command '${commandName}' not found in service '${serviceName}'`);
      }

      // Check if we have the right number of parameters
      console.log(`Command expects ${command.parameters.length} parameters, got ${params.length}`);
      console.log(`Command parameters:`, command.parameters);
      console.log(`Provided parameters:`, params);
      
      if (command.parameters.length !== params.length) {
        console.error(`Command '${commandName}' expects ${command.parameters.length} parameters, but got ${params.length}`);
        throw new Error(`Command '${commandName}' expects ${command.parameters.length} parameters, but got ${params.length}`);
      }

      // Execute the command with the provided parameters
      console.log(`Executing ${serviceName}.${commandName} with params:`, params);
      const result = await command.method(...params);
      console.log(`Command result:`, result);
      return result.data || result;
    } catch (error) {
      console.error(`Error executing command ${serviceName}.${commandName}:`, error);
      throw error;
    }
  }

  /**
   * Get a list of all available commands in a format suitable for AI
   * @returns {Array} Array of command descriptions
   */
  getCommandsForAI() {
    const commandsList = [];

    // Iterate through all services and commands
    Object.entries(this.commands).forEach(([serviceName, serviceCommands]) => {
      Object.entries(serviceCommands).forEach(([commandName, commandDetails]) => {
        commandsList.push({
          command: `${serviceName}.${commandName}`,
          description: commandDetails.description,
          parameters: commandDetails.parameters,
          example: commandDetails.example
        });
      });
    });

    return commandsList;
  }
}

export default new AICommandService(); 